import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CoachIA from "@/components/CoachIA";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import {
  Target, Map, Brain, Gamepad2, Rocket, Award, ChevronRight, ArrowLeft, Sparkles,
  Loader2, Check, X, RotateCcw, BookMarked, ExternalLink, Languages,
} from "lucide-react";

const STEP_META = [
  { key: "mission", icon: Target, color: "#E5276A" },
  { key: "explorer", icon: Map, color: "#1F3A5F" },
  { key: "comprendre", icon: Brain, color: "#F59E0B" },
  { key: "train", icon: Gamepad2, color: "#10B981" },
  { key: "challenge", icon: Rocket, color: "#8B5CF6" },
  { key: "validation", icon: Award, color: "#E5276A" },
];

// Deterministic shuffle (Fisher-Yates seeded on question id) so the layout
// stays stable across renders but differs per question.
function seededShuffle(arr, seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const rng = () => { h = (h * 1664525 + 1013904223) | 0; return ((h >>> 0) % 10000) / 10000; };
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Convert per-kind answer state into the string payload expected by the
// backend `/submit` endpoint. Returns "" when the answer isn't ready yet.
function normalizeAnswer(q, raw) {
  const kind = q?.kind || "qcm";
  if (kind === "matching") {
    const pairs = q.pairs || [];
    const map = raw && typeof raw === "object" ? raw : {};
    // Require every left to have a right chosen before we treat the answer as ready.
    if (pairs.some((p) => !map[p.left])) return "";
    return JSON.stringify(pairs.map((p) => ({ left: p.left, right: map[p.left] })));
  }
  if (kind === "ordering") {
    const items = q.items || [];
    if (!Array.isArray(raw) || raw.length !== items.length) return "";
    return JSON.stringify(raw);
  }
  return raw || "";
}

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useT();
  const [course, setCourse] = useState(null);
  const [content, setContent] = useState(null);
  const [step, setStep] = useState("mission");
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [certificate, setCertificate] = useState(null);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const cRes = await api.get(`/academy/courses/${slug}?lang=${lang}`);
        setCourse(cRes.data);
        if (user && !cRes.data.progress?.started) {
          await api.post(`/academy/courses/${slug}/start`).catch(() => {});
        }
        const contentRes = await api.get(`/academy/courses/${slug}/content?lang=${lang}`);
        setContent(contentRes.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("course.loadError"));
      } finally { setBusy(false); }
    })();
  }, [slug, lang, user, t]);

  const submitQuiz = async (quizStep, questions) => {
    if (!user) { navigate(`/login?next=/courses/${slug}`); return; }
    const answersArr = questions.map((q) => ({
      question_id: q.id,
      answer: normalizeAnswer(q, answers[q.id]),
    }));
    if (answersArr.some((a) => !a.answer)) {
      toast.error(t("course.quiz.answerAll")); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/academy/courses/${slug}/submit`, {
        step: quizStep,
        answers: answersArr,
      });
      setFeedback(data);
      if (data.srs_added > 0) {
        toast.success(t("course.quiz.srsAdded").replace("{n}", data.srs_added), { duration: 4000 });
      }
      if (data.certificate) setCertificate(data.certificate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("course.loadError"));
    } finally { setSubmitting(false); }
  };

  const goNext = () => {
    const idx = STEP_META.findIndex((s) => s.key === step);
    if (idx < STEP_META.length - 1) {
      setStep(STEP_META[idx + 1].key);
      setFeedback(null);
      setAnswers({});
    }
  };
  const resetStep = () => { setFeedback(null); setAnswers({}); };

  if (busy) return <div className="max-w-4xl mx-auto px-5 py-16 text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("course.loading")}</div>;
  if (!course || !content) return <div className="max-w-4xl mx-auto px-5 py-16 text-[#8B95A1]">{t("course.notFound")}</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="course-detail">
      <Link to="/courses" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E5276A]">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("course.back")}
      </Link>

      {/* HERO */}
      <div className="mt-6 rounded-3xl p-5 md:p-7 relative overflow-hidden"
        style={{
          background: course.cover_url
            ? `linear-gradient(135deg, ${course.color_hex}CC 0%, ${course.color_hex}66 60%, rgba(255,255,255,0.4) 100%), url(${(process.env.REACT_APP_BACKEND_URL || "")}${course.cover_url}) center/cover`
            : `linear-gradient(135deg, ${course.color_hex}55 0%, ${course.color_hex}22 100%)`,
        }}
      >
        <div className="flex items-start gap-3">
          <div className="text-4xl md:text-5xl leading-none shrink-0">{course.emoji}</div>
          <div className="min-w-0">
            <span className="inline-block text-[10px] uppercase tracking-widest bg-[#1F3A5F] text-white px-2 py-1 rounded-full font-bold">
              {course.level_label} · {course.region}
            </span>
            <h1 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-2 leading-tight font-medium">{course.title}</h1>
            <p className="text-[#4A5568] mt-1.5 text-sm md:text-base max-w-2xl">{course.subtitle}</p>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#E5276A]">
              <Sparkles className="w-3.5 h-3.5" /> {t("course.xp").replace("{xp}", course.xp_reward).replace("{min}", course.duration_min)}
            </div>
          </div>
        </div>
      </div>

      {course.translation_fallback && (
        <div
          data-testid="course-translation-fallback-banner"
          className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-[13px] text-amber-900"
        >
          <Languages className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
          <div>
            <b>{t("course.fallback.title")}</b> {t("course.fallback.body")}
          </div>
        </div>
      )}

      {/* PROGRESS BAR / STEPPER */}
      <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2 -mx-2 px-2" data-testid="course-stepper">
        {STEP_META.map((s, i) => {
          const active = step === s.key;
          const done = STEP_META.findIndex((x) => x.key === step) > i;
          return (
            <button
              key={s.key}
              onClick={() => setStep(s.key)}
              data-testid={`course-step-${s.key}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all ${
                active ? "bg-[#1F3A5F] text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-[#FBF7F3] text-[#8B95A1] hover:bg-[#FCE4ED] hover:text-[#E5276A]"
              }`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {t(`course.step.${s.key}`)}
              {done && <Check className="w-3 h-3" />}
            </button>
          );
        })}
      </div>

      {/* STEP BODIES */}
      <div className="mt-8">
        {step === "mission" && (
          <StepMission content={content} onNext={goNext} t={t} />
        )}
        {step === "explorer" && (
          <StepExplorer course={course} onNext={goNext} t={t} />
        )}
        {step === "comprendre" && (
          <StepComprendre content={content} onNext={goNext} slug={slug} requireAuth={!user} t={t} />
        )}
        {step === "train" && (
          <StepQuiz
            title={t("course.train.title")}
            subtitle={t("course.train.sub")}
            questions={content.train_questions || []}
            answers={answers}
            setAnswers={setAnswers}
            feedback={feedback}
            submitting={submitting}
            onSubmit={() => submitQuiz("train", content.train_questions || [])}
            onReset={resetStep}
            onNext={goNext}
            requireAuth={!user}
            slug={slug}
            navigate={navigate}
            t={t}
          />
        )}
        {step === "challenge" && (
          <StepChallenge
            scenario={content.challenge_scenario || {}}
            answer={answers.challenge}
            setAnswer={(v) => setAnswers({ challenge: v })}
            feedback={feedback}
            submitting={submitting}
            onSubmit={() => submitQuiz("challenge", [{ id: "challenge" }])}
            onReset={resetStep}
            onNext={goNext}
            requireAuth={!user}
            slug={slug}
            navigate={navigate}
            t={t}
          />
        )}
        {step === "validation" && (
          <StepQuiz
            title={t("course.validation.title")}
            subtitle={t("course.validation.sub")}
            questions={content.validation_questions || []}
            answers={answers}
            setAnswers={setAnswers}
            feedback={feedback}
            submitting={submitting}
            onSubmit={() => submitQuiz("validation", content.validation_questions || [])}
            onReset={resetStep}
            onNext={goNext}
            certificate={certificate}
            slug={slug}
            navigate={navigate}
            requireAuth={!user}
            finalStep
            t={t}
          />
        )}

        {/* Contextual AI Coach — anchored to the current step, keeps the same session across steps */}
        <CoachIA slug={slug} step={step} loggedIn={!!user} />
      </div>
    </div>
  );
}

/* ═════════ STEP COMPONENTS ═════════ */

function StepMission({ content, onNext, t }) {
  return (
    <div className="app-card p-6 md:p-8" data-testid="step-mission">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("course.step.mission")}</div>
      <h2 className="font-display text-3xl text-[#1F3A5F] mt-2">{t("course.mission.title")}</h2>
      <div className="mt-4 text-[#4A5568] text-lg leading-relaxed" data-testid="mission-body">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h3 className="font-display font-bold text-2xl md:text-3xl text-[#1F3A5F] mt-10 mb-4 leading-tight">{children}</h3>
            ),
            h2: ({ children }) => (
              <h3 className="font-display font-bold text-2xl md:text-3xl text-[#1F3A5F] mt-10 mb-4 leading-tight">{children}</h3>
            ),
            h3: ({ children }) => (
              <h4 className="font-display font-bold text-xl md:text-2xl text-[#1F3A5F] mt-8 mb-3 leading-tight">{children}</h4>
            ),
            h4: ({ children }) => (
              <h5 className="font-display font-bold text-lg text-[#1F3A5F] mt-6 mb-2">{children}</h5>
            ),
            p: ({ children }) => (
              <p className="text-[#4A5568] leading-[1.75] mb-5">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc pl-6 space-y-2 text-[#4A5568] mb-5 leading-relaxed">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-6 space-y-2 text-[#4A5568] mb-5 leading-relaxed">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => (
              <strong className="text-[#1F3A5F] font-bold">{children}</strong>
            ),
            em: ({ children }) => <em className="text-[#1F3A5F]/85">{children}</em>,
            a: ({ href, children }) => (
              <a href={href} className="text-[#E5276A] font-bold hover:underline" target="_blank" rel="noreferrer">{children}</a>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-[#E5276A] bg-[#FBF7F3] pl-4 pr-4 py-3 my-6 italic text-[#1F3A5F]">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-8 border-t border-[#1F3A5F]/10" />,
          }}
        >{content.mission_body}</ReactMarkdown>
      </div>
      <button onClick={onNext} data-testid="step-mission-next"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
        {t("course.mission.cta")} <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function StepExplorer({ course, onNext, t }) {
  const rawSlugs = (course.appellation_slugs && course.appellation_slugs.length > 0)
    ? course.appellation_slugs
    : (course.appellation_slug ? [course.appellation_slug] : []);
  // Normalise course-provided slugs (some legacy entries stored the display
  // name like "Chablis Grand Cru" instead of the URL slug "chablis-grand-cru")
  // and de-duplicate case-insensitively so the same appellation never appears
  // twice in the tab bar.
  const slugs = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const s of rawSlugs) {
      const norm = String(s || "").trim().toLowerCase().replace(/\s+/g, "-");
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        out.push(norm);
      }
    }
    return out;
  }, [rawSlugs]);
  const labelOf = (s) => s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  const [activeSlug, setActiveSlug] = useState(slugs[0] || "");
  const isMulti = slugs.length > 1;
  return (
    <div className="app-card p-6 md:p-8" data-testid="step-explorer">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("course.step.explorer")}</div>
      <h2 className="font-display text-3xl text-[#1F3A5F] mt-2">{isMulti ? t("course.explorer.titleMulti") : t("course.explorer.titleSingle")}</h2>
      <p className="text-[#4A5568] mt-2">
        {isMulti
          ? t("course.explorer.subMulti").replace("{n}", slugs.length)
          : t("course.explorer.subSingle")}
      </p>

      {isMulti && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="step-explorer-tabs">
          {slugs.map((s) => (
            <button
              key={s}
              onClick={() => setActiveSlug(s)}
              data-testid={`step-explorer-tab-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                activeSlug === s
                  ? "bg-[#1F3A5F] text-white"
                  : "bg-[#FBF7F3] text-[#1F3A5F] hover:bg-[#FCE4ED] hover:text-[#E5276A]"
              }`}
            >
              {labelOf(s)}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4">
        {activeSlug ? (
          <>
            <AppellationFicheCard slug={activeSlug} t={t} />
            <AtlasBlocksSection
              key={activeSlug}
              entityType="appellation"
              slug={activeSlug}
              emptyLabel={labelOf(activeSlug)}
              hideEmpty
            />
          </>
        ) : (
          <div className="text-sm text-[#8B95A1] italic">
            {t("course.explorer.subSingle")}
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center justify-between flex-wrap gap-2 border-t border-[#1F3A5F]/8 pt-4">
        {activeSlug && (
          <Link
            to={`/appellations/${activeSlug}`}
            target="_blank"
            data-testid={`step-explorer-open-${activeSlug}`}
            className="inline-flex items-center gap-1 text-xs text-[#4A5568] hover:text-[#E5276A]"
          >
            {t("course.explorer.openTab")} — <b className="ml-1">{labelOf(activeSlug)}</b> <ExternalLink className="w-3 h-3" />
          </Link>
        )}
        <button onClick={onNext} data-testid="step-explorer-next"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870] ml-auto">
          {t("course.explorer.next")} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function AppellationFicheCard({ slug, t }) {
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  useEffect(() => {
    let cancelled = false;
    setState("loading");
    api.get(`/appellations/${slug}`)
      .then((r) => { if (!cancelled) { setDoc(r.data); setState("ok"); } })
      .catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="app-card p-5 mb-4 animate-pulse">
        <div className="h-4 bg-[#F0EAE0] rounded w-1/3 mb-3"></div>
        <div className="h-3 bg-[#F0EAE0] rounded w-full mb-2"></div>
        <div className="h-3 bg-[#F0EAE0] rounded w-4/5"></div>
      </div>
    );
  }
  if (state === "error" || !doc) return null;

  const producers = Array.isArray(doc.producers) ? doc.producers.filter(Boolean) : [];
  const grapes = Array.isArray(doc.authorized_grapes) ? doc.authorized_grapes.filter(Boolean) : [];
  return (
    <div className="app-card p-0 mb-6 overflow-hidden border-l-4 border-[#E5276A]" data-testid={`explorer-fiche-${slug}`}>
      {doc.image && (
        <div className="w-full h-48 md:h-56 bg-[#FBF7F3] overflow-hidden">
          <img src={doc.image} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-5 md:p-6 space-y-5">
        <header>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">
                {doc.type || t("course.explorer.ficheLabel")}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-1">{doc.name}</h3>
            </div>
            {doc.region && (
              <div className="text-xs text-[#4A5568]">
                {doc.region}{doc.country ? ` · ${doc.country}` : ""}
              </div>
            )}
          </div>
          {doc.description && (
            <p className="mt-3 text-[15px] text-[#1F3A5F] leading-relaxed">{doc.description}</p>
          )}
        </header>

        <div className="grid gap-5 md:grid-cols-2">
          {doc.terroir && (
            <FicheField label={t("course.explorer.ficheTerroir")} value={doc.terroir} />
          )}
          {grapes.length > 0 && (
            <FicheField
              label={t("course.explorer.ficheGrapes")}
              value={grapes.join(" · ")}
            />
          )}
          {doc.regulation && (
            <div className="md:col-span-2">
              <FicheField
                label={t("course.explorer.ficheRegulation")}
                value={doc.regulation}
              />
            </div>
          )}
        </div>

        {producers.length > 0 && (
          <div>
            <div className="uppercase tracking-widest text-[#8B95A1] font-bold text-[10px] mb-2">
              {t("course.explorer.ficheProducers")} · {producers.length}
            </div>
            <div className="flex flex-wrap gap-2">
              {producers.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#FBF7F3] border border-[#E8DDD2] text-xs text-[#1F3A5F] font-medium"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}

        <a
          href={`/appellations/${doc.slug}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`explorer-fiche-open-${slug}`}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#E5276A] hover:underline"
        >
          {t("course.explorer.openTab")}
        </a>
      </div>
    </div>
  );
}

function FicheField({ label, value }) {
  return (
    <div className="text-sm">
      <div className="uppercase tracking-widest text-[#8B95A1] font-bold text-[10px] mb-1">{label}</div>
      <div className="text-[#1F3A5F] leading-relaxed whitespace-pre-line">{value}</div>
    </div>
  );
}

function StepComprendre({ content, onNext, slug, requireAuth, t }) {
  const [savingSRS, setSavingSRS] = useState(false);
  const [srsSaved, setSrsSaved] = useState(null);
  const saveToSRS = async () => {
    if (requireAuth) { toast.error(t("course.quiz.signInPrompt")); return; }
    setSavingSRS(true);
    try {
      const { data } = await api.post(`/academy/courses/${slug}/comprendre/save-to-srs`);
      setSrsSaved(data);
      if (data.added > 0) toast.success(t("course.comprendre.savedToast").replace("{n}", data.added), { duration: 4000 });
      else toast.info(t("course.comprendre.alreadyIn"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("course.loadError"));
    } finally { setSavingSRS(false); }
  };
  return (
    <div data-testid="step-comprendre">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("course.step.comprendre")}</div>
          <h2 className="font-display text-3xl text-[#1F3A5F] mt-2 mb-1">{t("course.comprendre.title")}</h2>
        </div>
        <button
          onClick={saveToSRS}
          disabled={savingSRS || srsSaved?.added === 0}
          data-testid="comprendre-save-srs"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border-2 border-[#1F3A5F]/15 hover:border-[#E5276A] text-xs font-bold text-[#1F3A5F] hover:text-[#E5276A] transition-colors disabled:opacity-50"
          title={t("course.comprendre.saveTitle")}
        >
          {savingSRS ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookMarked className="w-3.5 h-3.5" />}
          {srsSaved ? t("course.comprendre.inDeck").replace("{n}", srsSaved.total - srsSaved.added) : t("course.comprendre.saveLater")}
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {(content.comprendre_cards || []).map((card, i) => (
          <div key={i} className="app-card p-5 border-l-4 border-[#E5276A]" data-testid={`comprendre-card-${i}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold mb-1.5">{t("course.comprendre.key")} {i + 1}</div>
            <h3 className="font-display text-lg text-[#1F3A5F]">{card.title}</h3>
            <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>
      <button onClick={onNext} data-testid="step-comprendre-next"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
        {t("course.comprendre.cta")} <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function StepQuiz({
  title, subtitle, questions, answers, setAnswers, feedback, submitting,
  onSubmit, onReset, onNext, certificate, requireAuth, slug, navigate, finalStep, t,
}) {
  return (
    <div data-testid={finalStep ? "step-validation" : "step-train"}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{finalStep ? t("course.step.validation") : t("course.step.train")}</div>
      <h2 className="font-display text-3xl text-[#1F3A5F] mt-2">{title}</h2>
      <p className="text-[#4A5568] mt-2 mb-6">{subtitle}</p>

      {requireAuth && (
        <div className="app-card p-5 mb-6 border-l-4 border-[#E5276A] bg-[#FCE4ED]/40">
          <p className="text-sm text-[#1F3A5F]">{t("course.quiz.signInPrompt")}</p>
          <button onClick={() => navigate(`/login?next=/courses/${slug}`)} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold">
            {t("course.quiz.signIn")} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {questions.map((q, i) => (
          <QuestionCard
            key={q.id || i}
            q={q}
            idx={i}
            selected={answers[q.id]}
            onSelect={(val) => !feedback && setAnswers({ ...answers, [q.id]: val })}
            feedback={feedback}
            disabled={requireAuth}
            t={t}
          />
        ))}
      </div>

      {feedback && (
        <div className={`mt-6 p-5 rounded-2xl border-l-4 ${feedback.score >= 60 ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"}`} data-testid="quiz-feedback">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-display text-[#1F3A5F]">{feedback.score}%</div>
            <div className="min-w-0">
              <div className="font-bold text-[#1F3A5F]">
                {t("course.quiz.scoreLine")
                  .replace("{right}", feedback.right)
                  .replace("{total}", feedback.total)
                  .replace("{xp}", feedback.xp_awarded)}
              </div>
              <div className="text-xs text-[#4A5568] mt-0.5">
                {feedback.passed_course === false && feedback.message}
                {feedback.passed_course === true && t("course.quiz.certified")}
              </div>
              {feedback.srs_message && (
                <div className="text-xs text-[#1F3A5F] mt-1 inline-flex items-center gap-1">
                  <BookMarked className="w-3 h-3" /> {feedback.srs_message}
                </div>
              )}
            </div>
          </div>

          {certificate && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/courses/${slug}/certificate/${certificate.id}`} data-testid="quiz-view-cert"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                <Award className="w-4 h-4" /> {t("course.quiz.viewCert")}
              </Link>
            </div>
          )}
          {!finalStep && feedback.score >= 50 && (
            <button onClick={onNext} data-testid="quiz-next-step"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
              {t("course.quiz.continue")} <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {feedback.score < 50 && (
            <button onClick={onReset} data-testid="quiz-retry"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
              <RotateCcw className="w-4 h-4" /> {t("course.quiz.retry")}
            </button>
          )}
        </div>
      )}

      {!feedback && (
        <button onClick={onSubmit} disabled={submitting || requireAuth} data-testid="quiz-submit"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("course.quiz.correcting")}</> : <><Check className="w-4 h-4" /> {t("course.quiz.submit")}</>}
        </button>
      )}
    </div>
  );
}

function StepChallenge({ scenario, answer, setAnswer, feedback, submitting, onSubmit, onReset, onNext, requireAuth, slug, navigate, t }) {
  const choices = scenario.choices || [];
  return (
    <div data-testid="step-challenge">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("course.step.challenge")}</div>
      <h2 className="font-display text-3xl text-[#1F3A5F] mt-2">{scenario.title || t("course.challenge.title")}</h2>
      <div className="app-card p-6 mt-4 border-l-4 border-[#8B5CF6]">
        <p className="text-[#1F3A5F] italic leading-relaxed">{scenario.situation}</p>
        <p className="text-[#4A5568] mt-4 font-bold">{scenario.prompt}</p>
        <div className="mt-4 space-y-2">
          {choices.map((c) => {
            const letter = (c.split(".")[0] || "").trim().toUpperCase();
            const isSel = answer === letter;
            const isRight = feedback && feedback.details?.[0]?.correct === letter;
            const isWrong = feedback && isSel && !feedback.details?.[0]?.ok;
            return (
              <button
                key={letter}
                onClick={() => !feedback && setAnswer(letter)}
                disabled={!!feedback || requireAuth}
                data-testid={`challenge-choice-${letter}`}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                  feedback && isRight ? "bg-emerald-50 border-emerald-500 text-emerald-900" :
                  isWrong ? "bg-red-50 border-red-500 text-red-900" :
                  isSel ? "bg-[#FCE4ED] border-[#E5276A]" : "bg-white border-[#1F3A5F]/12 hover:border-[#E5276A]"
                } disabled:cursor-not-allowed`}
              >
                <span className="text-sm text-[#1F3A5F]">{c}</span>
              </button>
            );
          })}
        </div>
        {feedback && (
          <div className="mt-5 pt-5 border-t border-[#1F3A5F]/8">
            <div className={`flex items-center gap-2 ${feedback.details?.[0]?.ok ? "text-emerald-700" : "text-red-700"} font-bold`}>
              {feedback.details?.[0]?.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {feedback.details?.[0]?.ok
                ? t("course.challenge.correct")
                : t("course.challenge.wrong").replace("{letter}", feedback.details?.[0]?.correct || "")}
              <span className="text-[#8B95A1] ml-2">+{feedback.xp_awarded} XP</span>
            </div>
            <p className="text-sm text-[#4A5568] mt-2">{feedback.details?.[0]?.explanation}</p>
            <button onClick={onNext} data-testid="challenge-next"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
              {t("course.challenge.next")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {requireAuth && (
        <div className="app-card p-5 mt-4 border-l-4 border-[#E5276A] bg-[#FCE4ED]/40">
          <p className="text-sm text-[#1F3A5F]">{t("course.challenge.signInPrompt")}</p>
          <button onClick={() => navigate(`/login?next=/courses/${slug}`)} className="mt-3 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold">{t("course.quiz.signIn")}</button>
        </div>
      )}

      {!feedback && !requireAuth && (
        <button onClick={onSubmit} disabled={!answer || submitting} data-testid="challenge-submit"
          className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("course.quiz.correcting")}</> : <><Check className="w-4 h-4" /> {t("course.challenge.submit")}</>}
        </button>
      )}
    </div>
  );
}

function QuestionCard({ q, idx, selected, onSelect, feedback, disabled, t }) {
  const detail = feedback?.details?.find((d) => d.question_id === q.id);
  const kind = q.kind || "qcm";
  return (
    <div className="app-card p-5" data-testid={`quiz-question-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-[#1F3A5F] text-white flex items-center justify-center text-xs font-bold shrink-0">{idx + 1}</div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-[#1F3A5F]">{q.question}</p>
          {kind === "matching" && (
            <MatchingBody q={q} selected={selected} onSelect={onSelect} detail={detail} disabled={disabled} t={t} />
          )}
          {kind === "ordering" && (
            <OrderingBody q={q} selected={selected} onSelect={onSelect} detail={detail} disabled={disabled} t={t} />
          )}
          {kind !== "matching" && kind !== "ordering" && (
            <McqBody q={q} selected={selected} onSelect={onSelect} detail={detail} disabled={disabled} feedback={feedback} />
          )}
          {detail && detail.explanation && (
            <p className={`text-xs mt-3 italic ${detail.ok ? "text-emerald-800" : "text-red-800"}`}>{detail.explanation}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function McqBody({ q, selected, onSelect, detail, disabled, feedback }) {
  return (
    <div className="mt-3 space-y-2">
      {(q.choices || []).map((c) => {
        const letter = (c.split(".")[0] || "").trim().toUpperCase();
        const isSel = selected === letter;
        const isRight = detail && detail.correct === letter;
        const isWrong = detail && isSel && !detail.ok;
        return (
          <button
            key={letter}
            onClick={() => onSelect(letter)}
            disabled={!!feedback || disabled}
            data-testid={`quiz-choice-${q.id}-${letter}`}
            className={`w-full text-left px-3.5 py-2.5 rounded-lg border text-sm transition-all ${
              detail && isRight ? "bg-emerald-50 border-emerald-500 text-emerald-900" :
              isWrong ? "bg-red-50 border-red-500 text-red-900" :
              isSel ? "bg-[#FCE4ED] border-[#E5276A] text-[#1F3A5F]" : "bg-white border-[#1F3A5F]/12 hover:border-[#E5276A] text-[#1F3A5F]"
            } disabled:cursor-not-allowed`}
          >
            {c}
          </button>
        );
      })}
    </div>
  );
}

function MatchingBody({ q, selected, onSelect, detail, disabled, t }) {
  const pairs = q.pairs || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rightOptions = React.useMemo(
    () => seededShuffle(pairs.map((p) => p.right), q.id || ""),
    [q.id]
  );
  const map = selected && typeof selected === "object" ? selected : {};
  const locked = !!detail || disabled;

  // Build correctness map by decoding the "left → right · left → right" display.
  const correctMap = React.useMemo(() => {
    if (!detail?.correct) return {};
    const out = {};
    detail.correct.split("·").forEach((seg) => {
      const [l, r] = seg.split("→").map((s) => (s || "").trim());
      if (l && r) out[l] = r;
    });
    return out;
  }, [detail]);

  return (
    <>
      <p className="text-xs text-[#4A5568] mt-2 mb-3">{t("course.matching.hint")}</p>
      <div className="space-y-2">
        {pairs.map((p) => {
          const chosen = map[p.left] || "";
          const good = detail && chosen && correctMap[p.left] === chosen;
          const bad = detail && chosen && correctMap[p.left] !== chosen;
          return (
            <div key={p.left} className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-[#FBF7F3] border border-[#E8DDD2] text-sm text-[#1F3A5F] font-medium">
                {p.left}
              </div>
              <span className="text-[#8B95A1]">→</span>
              <select
                data-testid={`quiz-match-${q.id}-${p.left}`}
                value={chosen}
                onChange={(e) => onSelect({ ...map, [p.left]: e.target.value })}
                disabled={locked}
                className={`flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm transition-all ${
                  good ? "bg-emerald-50 border-emerald-500 text-emerald-900" :
                  bad ? "bg-red-50 border-red-500 text-red-900" :
                  chosen ? "bg-[#FCE4ED] border-[#E5276A] text-[#1F3A5F]" :
                  "bg-white border-[#1F3A5F]/12 text-[#1F3A5F]"
                } disabled:cursor-not-allowed`}
              >
                <option value="">{t("course.matching.placeholder")}</option>
                {rightOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {bad && (
                <div className="w-full text-xs text-emerald-700 pl-1 mt-1">
                  <span className="font-bold">{t("course.quiz.correctAnswer")} :</span> {correctMap[p.left]}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {detail && !detail.ok && detail.score > 0 && (
        <p className="text-xs mt-3 text-amber-700 font-bold">
          {t("course.quiz.partial")} · {Math.round((detail.score || 0) * 100)}%
        </p>
      )}
    </>
  );
}

function OrderingBody({ q, selected, onSelect, detail, disabled, t }) {
  const items = q.items || [];
  const order = Array.isArray(selected) && selected.length === items.length
    ? selected
    : seededShuffle(items, q.id || "");
  const locked = !!detail || disabled;

  // Initialise state once so the guard/submit sees a value.
  React.useEffect(() => {
    if (!Array.isArray(selected) || selected.length !== items.length) {
      onSelect(order);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    onSelect(next);
  };

  const expected = items;
  return (
    <>
      <p className="text-xs text-[#4A5568] mt-2 mb-3">{t("course.ordering.hint")}</p>
      <div className="space-y-2">
        {order.map((it, i) => {
          const good = detail && expected[i] === it;
          const bad = detail && expected[i] !== it;
          return (
            <div
              key={`${it}-${i}`}
              data-testid={`quiz-order-${q.id}-${i}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                good ? "bg-emerald-50 border-emerald-500 text-emerald-900" :
                bad ? "bg-red-50 border-red-500 text-red-900" :
                "bg-white border-[#1F3A5F]/12 text-[#1F3A5F]"
              }`}
            >
              <div className="w-6 h-6 rounded-full bg-[#1F3A5F] text-white text-[11px] flex items-center justify-center font-bold shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">{it}</div>
              <button
                type="button"
                aria-label={t("course.ordering.moveUp")}
                data-testid={`quiz-order-up-${q.id}-${i}`}
                disabled={locked || i === 0}
                onClick={() => move(i, -1)}
                className="w-7 h-7 rounded-md border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
              >↑</button>
              <button
                type="button"
                aria-label={t("course.ordering.moveDown")}
                data-testid={`quiz-order-down-${q.id}-${i}`}
                disabled={locked || i === order.length - 1}
                onClick={() => move(i, 1)}
                className="w-7 h-7 rounded-md border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
              >↓</button>
            </div>
          );
        })}
      </div>
      {detail && !detail.ok && (
        <div className="mt-3 text-xs text-[#1F3A5F]">
          <span className="font-bold">{t("course.quiz.expectedOrder")}</span>{" "}
          <span className="text-emerald-700">{expected.join(" → ")}</span>
          {detail.score > 0 && (
            <span className="ml-2 text-amber-700 font-bold">· {t("course.quiz.partial")} {Math.round((detail.score || 0) * 100)}%</span>
          )}
        </div>
      )}
    </>
  );
}
